// Runtime detection of which app to render (therapist vs patient vs israel).
//
// The therapist app, the (UK) patient/psyara app and the (Israel) patient app
// all share a single build/bundle. Which UI is mounted is decided at runtime
// from the hostname, so patients on their own domain never reach the therapist
// login.
//
// Configuration:
//   REACT_APP_PATIENT_HOSTNAMES  comma-separated list of hostnames that should
//                                render the (UK) patient app (overrides the
//                                defaults below when set at build time).
//   REACT_APP_ISRAEL_HOSTNAMES   comma-separated list of hostnames that should
//                                render the Israel patient app (overrides the
//                                defaults below when set at build time).
//   REACT_APP_FORCE_APP          "patient" | "therapist" | "israel" — force a
//                                mode locally (useful for
//                                `REACT_APP_FORCE_APP=israel npm start`).
//   ?app=patient | ?app=therapist | ?app=israel  one-off override; persisted to
//                                sessionStorage so it survives client-side nav.

export type AppTarget = 'therapist' | 'patient' | 'israel';

const DEFAULT_PATIENT_HOSTNAMES = [
  'psyara-health.co.uk',      // prod patient domain (apex)
  'www.psyara-health.co.uk',  // prod patient domain (www)
  'dev.psyara-health.co.uk',  // dev patient domain
];

const DEFAULT_ISRAEL_HOSTNAMES = [
  'dev-my.taliaz-cm.com',     // dev Israel patient domain
  'my.taliaz-cm.com',         // prod Israel patient domain
];

function patientHostnames(): string[] {
  const fromEnv = (process.env.REACT_APP_PATIENT_HOSTNAMES || '')
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
  return fromEnv.length > 0 ? fromEnv : DEFAULT_PATIENT_HOSTNAMES;
}

function israelHostnames(): string[] {
  const fromEnv = (process.env.REACT_APP_ISRAEL_HOSTNAMES || '')
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
  return fromEnv.length > 0 ? fromEnv : DEFAULT_ISRAEL_HOSTNAMES;
}

function isValidTarget(value: string | null | undefined): value is AppTarget {
  return value === 'patient' || value === 'therapist' || value === 'israel';
}

function readOverride(): AppTarget | null {
  // Build-time / env override (local dev).
  if (isValidTarget(process.env.REACT_APP_FORCE_APP)) {
    return process.env.REACT_APP_FORCE_APP as AppTarget;
  }

  // Query-param override, persisted so it survives in-app navigation.
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('app');
    if (isValidTarget(fromQuery)) {
      window.sessionStorage.setItem('appMode', fromQuery);
      return fromQuery;
    }
    const stored = window.sessionStorage.getItem('appMode');
    if (isValidTarget(stored)) return stored;
  } catch {
    // sessionStorage may be unavailable; fall through to hostname detection.
  }
  return null;
}

// Resolve which app should be mounted for the current request.
export function resolveApp(): AppTarget {
  const override = readOverride();
  if (override) return override;

  const hostname = window.location.hostname.toLowerCase();
  if (israelHostnames().includes(hostname)) return 'israel';
  if (patientHostnames().includes(hostname)) return 'patient';
  return 'therapist';
}

// patient = the UK psyara patient app.
export function isPatientApp(): boolean {
  return resolveApp() === 'patient';
}

// israel = the Israel-clinic patient app.
export function isIsraelApp(): boolean {
  return resolveApp() === 'israel';
}

// Which staff app a login belongs to. The clinic and HMO apps share one
// backend, and the backend gates login per app: the clinic app needs the
// clinic_login privilege flag, the HMO app needs the hmo flag.
//
//   REACT_APP_HMO_HOSTNAMES  comma-separated hostnames served by the HMO app
//                            (overrides the defaults below when set).
//   ?loginApp=hmo | ?loginApp=clinic  one-off override for local testing.
export type LoginApp = 'clinic' | 'hmo';

const DEFAULT_HMO_HOSTNAMES = [
  'dev-hmo.taliaz-cm.com',    // dev HMO domain
  'hmo.taliaz-cm.com',        // prod HMO domain
];

function hmoHostnames(): string[] {
  const fromEnv = (process.env.REACT_APP_HMO_HOSTNAMES || '')
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
  return fromEnv.length > 0 ? fromEnv : DEFAULT_HMO_HOSTNAMES;
}

export function resolveLoginApp(): LoginApp {
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('loginApp');
    if (fromQuery === 'hmo' || fromQuery === 'clinic') return fromQuery;
  } catch {
    // Fall through to hostname detection.
  }
  return hmoHostnames().includes(window.location.hostname.toLowerCase()) ? 'hmo' : 'clinic';
}

// ── Which TENANT this host serves ───────────────────────────────────────────
//
// A third, independent question, and deliberately not part of AppTarget: that
// one chooses which PRODUCT mounts (patient app, therapist app, Israel patient
// app), while this host mounts the ordinary clinic app and only changes how
// somebody signs in to it.
//
// The Israel-tenant host belongs to Israeli staff, who have no password here -
// Israel owns their identity. So that host never shows a login form: an
// anonymous visitor is sent to the Israeli system and comes back signed in, and
// so is anybody whose session has expired.
//
// Overrides:
//   REACT_APP_IL_TENANT_HOSTNAMES  comma-separated hostnames for the Israel
//                                  tenant (replaces the defaults below).
//   REACT_APP_IL_CLINIC_UI_URL     where to send people to sign in.
//   ?tenantMode=il | ?tenantMode=uk   one-off override, for testing the
//                                     redirect on an ordinary host.
export type TenantMode = 'uk' | 'il';

const DEFAULT_IL_TENANT_HOSTNAMES = [
  'dev-ilpsy.taliaz-cm.com',  // dev Israel-tenant domain
  'ilpsy.taliaz-cm.com',      // prod Israel-tenant domain
];

// Where each of those sends people to sign in. Kept beside the hostnames
// rather than in one build-time variable because the production deploy step
// exports no REACT_APP_* at all, so a single value could not differ per
// environment - and sending production staff to the dev login would be a
// quiet, confusing failure.
const IL_CLINIC_UI_FOR_HOST: Record<string, string> = {
  'dev-ilpsy.taliaz-cm.com': 'https://dev-clinicui.taliaz-cm.com',
  'ilpsy.taliaz-cm.com': 'https://clinicui.taliaz-cm.com',
};

function ilTenantHostnames(): string[] {
  const fromEnv = (process.env.REACT_APP_IL_TENANT_HOSTNAMES || '')
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
  return fromEnv.length > 0 ? fromEnv : DEFAULT_IL_TENANT_HOSTNAMES;
}

export function resolveTenantMode(): TenantMode {
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('tenantMode');
    if (fromQuery === 'il' || fromQuery === 'uk') return fromQuery;
  } catch {
    // Fall through to hostname detection.
  }
  return ilTenantHostnames().includes(window.location.hostname.toLowerCase())
    ? 'il'
    : 'uk';
}

export function isIlTenantApp(): boolean {
  return resolveTenantMode() === 'il';
}

// Where this host's users sign in. Falls back to the dev clinicUI so an
// override-driven test on localhost has somewhere to go.
export function ilClinicUiUrl(): string {
  const configured = (process.env.REACT_APP_IL_CLINIC_UI_URL || '').trim();
  if (configured) return configured.replace(/\/+$/, '');
  const host = window.location.hostname.toLowerCase();
  return IL_CLINIC_UI_FOR_HOST[host] || 'https://dev-clinicui.taliaz-cm.com';
}

// Where somebody with a UK password signs in, for a host that has no form of
// its own. The Israel-tenant host is the Israeli door and only that; the
// ordinary clinic host serves the same app and the same tenants, so nothing is
// lost by leaving the form out there - it is simply at the other address.
const UK_LOGIN_FOR_IL_HOST: Record<string, string> = {
  'dev-ilpsy.taliaz-cm.com': 'dev-psyclinic.taliaz-cm.com',
  'ilpsy.taliaz-cm.com': 'psy.taliaz-cm.com',
};

export function ukLoginHost(): string {
  return UK_LOGIN_FOR_IL_HOST[window.location.hostname.toLowerCase()]
    || 'dev-psyclinic.taliaz-cm.com';
}
